import Head from "next/head";
import Link from "next/link";
import NextScript from "next/script";

import { routes } from "src/shared/routes";
import { openPdfInNewTab } from "src/utils/utils";
import { global_config } from "src/shared/global_config";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "Tickets are assigned for each game on your schedule the day of your game.",
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        You will receive a text when tickets are delivered to your Sharks
        account 3 hours prior to game start. Login to access and manage your
        tickets here{" "}
        <Link href={"https://am.ticketmaster.com/sjsharks/"} target="_blank">
          https://am.ticketmaster.com/sjsharks/
        </Link>
      </>
    ),
  },

  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "San Jose Sharks Breakaway Pass tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your San Jose Sharks Breakaway Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed.",
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 4 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. If available, you should see them available as an add-on.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        An active purchase for the month is not refundable. To cancel your pass
        for the upcoming month opt-out through the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab.
      </>
    ),
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer: (
      <>
        To update your payment method, simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        menu on the right side of your screen. You will see a section to update
        your payment method where you can update new information.
      </>
    ),
  },
  {
    question: "How do I renew my subscription?",
    answer: (
      <>
        Your subscription will automatically renew on the 26th of the month!
        There is no action needed on your end to keep your subscription active.
      </>
    ),
  },
  {
    question: "Can I pause my subscription?",
    answer: (
      <>
        Yes, once you navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab in your account you can elect to pause your subscription for that
        month during the opt out window from the 15th to the 26th. You will
        receive a text message when this period is open.
      </>
    ),
  },
  {
    question: "Can I cancel my subscription?",
    answer: (
      <>
        Simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab to cancel your subscription. You can only do so during the monthly
        opt out period from the 15th to the 26th of the month.
      </>
    ),
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: (
      <>We do not issue any refunds for games you are not able to attend.</>
    ),
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        An active purchase for the month is not refundable. To cancel your pass
        for the upcoming month opt-out through the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sanjosesharks_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExt();
  return (
    <>
      By purchasing you understand that you are agreeing to a monthly
      subscription. It will renew each month unless you cancel during the
      defined opt-out period. Tickets are non-transferable and not eligible for
      resale.
      {!mdUp ? (
        <>
          <br />
          <br />
        </>
      ) : (
        " "
      )}
      You also agree to receive SMS alerts regarding ticket delivery and agree
      to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  return (
    <>
      Opt out restricted t&c. By purchasing you understand that you are agreeing
      to a monthly subscription. It will renew each month unless you cancel
      during the defined opt-out period. Tickets are non-transferable and not
      eligible for resale.
    </>
  );
};

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      Tickets are non-transferable and not eligible for resale. You also agree
      to receive SMS alerts regarding ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-QRR3ZRR19C"
      />

      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-analytics-inline`}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'G-QRR3ZRR19C');
        `,
        }}
      />
    </Head>
  );
};
